import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { DatabaseHealth } from '../../../models/database-health';


/**
 * The blocking modal shown while Capitan's database is unreachable (PRO-1361).
 * Opened by ApiService non-dismissible, so it must clear itself: it polls the
 * check function and dismisses once the database answers, which is what keeps
 * an unattended check-in kiosk from sitting on a dead screen. An `unknown`
 * result (no answer from the API) is no information, not bad news: the modal
 * stays open, the copy stays neutral and the backoff is left alone.
 */
@Component({
  selector: 'app-database-maintenance-modal',
  templateUrl: './database-maintenance-modal.component.html',
  styleUrls: ['./database-maintenance-modal.component.css']
})
export class DatabaseMaintenanceModalComponent implements OnInit, OnDestroy {

  // Reports the database as up/down/unknown. Passed in rather than injected to
  // avoid a circular dependency between this and ApiService (the same reason
  // PinModalComponent takes verifyPinFunction).
  @Input() checkHealthFunction: () => Promise<DatabaseHealth>;

  // Called when the modal closes, so ApiService can clear its re-entrancy flag.
  @Input() closedCallback: () => undefined = null;

  // Seconds between automatic checks. Backs off up to MAX_INTERVAL_SECONDS so a
  // long outage isn't hammered by every open tab and kiosk in every gym. Only
  // a CONFIRMED-down answer advances it — see runCheck.
  private static readonly INITIAL_INTERVAL_SECONDS = 10;
  private static readonly MAX_INTERVAL_SECONDS = 60;
  private static readonly BACKOFF_FACTOR = 1.5;

  // Backstop above ApiService.checkDatabaseHealth's own request timeout: a
  // check that never settles would strand the poll loop of a modal that
  // cannot be dismissed.
  private static readonly CHECK_WATCHDOG_SECONDS = 20;

  // True while a check is in flight, so a failed Retry is visibly distinct
  // from a click that did nothing.
  public checking = false;

  // Set only when the API answered and confirmed the database is still
  // unreachable, so the "Still unavailable" copy is never shown on the
  // strength of a network error. Cleared while the next check runs.
  public lastCheckFailed = false;

  private intervalSeconds = DatabaseMaintenanceModalComponent.INITIAL_INTERVAL_SECONDS;
  private timeoutId: any = null;
  private watchdogId: any = null;
  private destroyed = false;

  constructor(
    public activeModal: NgbActiveModal
  ) {}

  public ngOnInit() {
    this.scheduleNextCheck();
  }

  public ngOnDestroy() {
    this.destroyed = true;
    this.clearPendingCheck();
    this.clearWatchdog();
    if (this.closedCallback !== null) {
      this.closedCallback();
    }
  }

  /**
   * Check now, from the Retry button. A check already in flight is left to
   * finish rather than being duplicated.
   */
  public handleRetry() {
    if (this.checking) {
      return;
    }
    this.clearPendingCheck();
    this.runCheck();
  }

  private scheduleNextCheck() {
    this.clearPendingCheck();
    this.timeoutId = setTimeout(
      () => { this.runCheck(); },
      this.intervalSeconds * 1000
    );
  }

  private clearPendingCheck() {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private runCheck() {
    this.checking = true;
    this.lastCheckFailed = false;

    // Whichever of the check and the watchdog lands first wins; the other is
    // ignored. Neither path may leave `checking` true or the loop unarmed.
    let settled = false;
    const finish = (result: DatabaseHealth) => {
      if (settled) {
        return;
      }
      settled = true;
      this.clearWatchdog();
      this.checking = false;

      if (this.destroyed) {
        return;
      }
      if (result === 'up') {
        this.activeModal.dismiss();
        return;
      }
      if (result === 'down') {
        // The API answered and told us the database is still unreachable.
        // Only this case may say so in the UI, and only this case backs off.
        this.lastCheckFailed = true;
        this.intervalSeconds = Math.min(
          this.intervalSeconds * DatabaseMaintenanceModalComponent.BACKOFF_FACTOR,
          DatabaseMaintenanceModalComponent.MAX_INTERVAL_SECONDS
        );
      }
      // 'unknown' falls through with nothing changed: a network problem must
      // not stretch the polling and delay noticing the database is back.
      this.scheduleNextCheck();
    };

    this.clearWatchdog();
    this.watchdogId = setTimeout(
      // A check that never answered is 'unknown', not 'down'.
      () => { finish('unknown'); },
      DatabaseMaintenanceModalComponent.CHECK_WATCHDOG_SECONDS * 1000
    );

    this.checkHealthFunction()
      .then(result => { finish(result); })
      // A check function that rejects told us nothing either.
      .catch(() => { finish('unknown'); });
  }

  private clearWatchdog() {
    if (this.watchdogId !== null) {
      clearTimeout(this.watchdogId);
      this.watchdogId = null;
    }
  }

}
